import { getErrorStatus } from "@/utils/httpStatus"
import Logger from "@/utils/Logger"

// useDataCacheCallback serves a stale value on ANY thrown error, so a 404 for a
// deleted/unpublished resource would render the last cached page. This wrapper
// uses the lower-level useDataCache so we own the catch: stale is served only
// when the upstream is unavailable (5xx / network / timeout), never for a 4xx.

interface CacheOptions<T> {
  maxAge: number
  staleIfError: number
  tags: string[] | ((data: T) => string[])
  // Same requestId the calling composable uses for its other logs, so a
  // stale-serve warning correlates with the rest of that request's log line.
  requestId?: string
}

/** 404 is 404: only an unavailable upstream (5xx, no status) may serve stale. */
export function shouldServeStaleForError(error: unknown): boolean {
  const status = getErrorStatus(error)
  return status === undefined || status < 400 || status >= 500
}

export async function useDataCacheWithStale<T>(key: string, fetcher: () => Promise<T>, options: CacheOptions<T>): Promise<T> {
  const cache = await useDataCache<T>(key)
  if (cache.value !== undefined)
    return cache.value

  try {
    const data = await fetcher()
    const tags = typeof options.tags === "function" ? options.tags(data) : options.tags
    await cache.addToCache(data, tags, options.maxAge, options.staleIfError)
    return data
  }
  catch (error) {
    if (cache.staleValue !== undefined && shouldServeStaleForError(error)) {
      // The upstream is unavailable but we have a usable cached copy. Serving it
      // is the intended behaviour, but it would otherwise be silent (the composable
      // sees a resolved value and logs success) — warn so the absorbed upstream
      // failure is still visible in monitoring.
      const status = getErrorStatus(error)
      Logger(options.requestId).warn(`Serving stale data cache for "${key}" after upstream error${status ? ` (status ${status})` : ""}`, error)
      return cache.staleValue
    }
    throw error
  }
}
